import { useMe } from '../organisation/useMe';
import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_RETRY_COUNT, reviewRequestsInternalApi } from '../apis';
import { InternalApiReviewRequestEntityType, InternalApiReviewStatus } from '../api-internal';
import { CAN_READ_REVIEW_REQUESTS } from '../auth/permissions';

export const REVIEW_REQUEST_KEY = 'review-request';
export const REVIEW_REQUESTS_KEY = 'review-requests';

export const useReviewRequests = (entityIds: string[] = [], entityType?: InternalApiReviewRequestEntityType) => {
  const { currentOrganisation, permissions } = useMe();

  return useQuery({
    queryKey: [REVIEW_REQUESTS_KEY, currentOrganisation?.id, entityIds, entityType],
    queryFn: async () => await reviewRequestsInternalApi.getList({ entityIds, entityType }),
    placeholderData: keepPreviousData,
    enabled: !!currentOrganisation?.id && permissions.includes(CAN_READ_REVIEW_REQUESTS),
  });
};

export const useReviewRequest = (entityType: InternalApiReviewRequestEntityType, entityId: string | null) => {
  const { currentOrganisation, permissions } = useMe();

  return useQuery({
    queryKey: [REVIEW_REQUEST_KEY, currentOrganisation?.id, entityId, entityType],
    queryFn: async () => {
      try {
        return await reviewRequestsInternalApi.findByEntityTypeAndEntityId({ entityType, entityId: entityId! });
      } catch (error) {
        if ((error as { status?: number }).status === 404) return null;
        throw error;
      }
    },
    enabled: !!currentOrganisation?.id && permissions.includes(CAN_READ_REVIEW_REQUESTS) && !!entityId,
    retry: (failureCount, error) => error.status !== 404 && failureCount < DEFAULT_RETRY_COUNT,
  });
};

export const useResolveReviewRequest = (onSettled?: () => void, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({
      entityId,
      entityType,
      status,
    }: {
      entityId: string;
      entityType: InternalApiReviewRequestEntityType;
      status: InternalApiReviewStatus;
    }) =>
      reviewRequestsInternalApi.resolve({
        internalApiResolveReviewRequest: { entityId, entityType, status },
      }),
    onSuccess: (_, { entityId, entityType }) => {
      invalidateReviewRequestQueries(queryClient, currentOrganisation?.id, entityId, entityType);
      onSuccess && onSuccess();
    },
    onSettled,
  });
};

export const invalidateReviewRequestQueries = (
  queryClient: QueryClient,
  organisationId: number | undefined,
  entityId: string,
  entityType: InternalApiReviewRequestEntityType
) => {
  queryClient.invalidateQueries({ queryKey: [REVIEW_REQUEST_KEY, organisationId, entityId, entityType] });
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) => {
      const [key, orgId, entityIds, type] = queryKey as [
        string,
        number | undefined,
        string[] | undefined,
        InternalApiReviewRequestEntityType | undefined,
      ];
      return (
        key === REVIEW_REQUESTS_KEY &&
        orgId === organisationId &&
        (!type || type === entityType) &&
        (!entityIds?.length || entityIds.includes(entityId))
      );
    },
  });
};
